import {
  type SIDE_PANELS_AVAILABLE,
  SubmissionsContext,
} from "@/components/DirectorySubmissions/Form/SubmissionsProvider";
import type { Section } from "@/components/DirectorySubmissions/Form/types";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Default empty matter object
const createEmptyMatter = () => ({
  "0": "",
  "1": "",
  "2": "",
  "3": "",
  "4": "",
  "5": "",
  "6": "",
  "7": "",
  "8": "",
});

// Enhanced context type for Attorney submissions
interface AttorneySubmissionsContextValue {
  formData: Record<string, any>;
  isLoaded: boolean;
  handleInputChange: (id: string, value: any) => void;
  handleInputBlur: (id: string, value?: any) => void;
  handleAddMatter: (type: "publishable" | "confidential") => void;
  handleDeleteMatter: (matterId: string) => void;
  clearAllData: () => void;
  updateSections: (sections: Section[]) => void;
}

const AttorneySubmissionsContext =
  createContext<AttorneySubmissionsContextValue | null>(null);

/**
 * Enhanced AttorneySubmissionsProvider that manages formData state internally
 * and handles localStorage persistence to prevent focus loss issues.
 */
const AttorneySubmissionsProvider: React.FC<{
  children: React.ReactNode;
  attorneyLinkId?: string;
  sections?: Section[];
}> = ({ children, attorneyLinkId, sections: initialSections = [] }) => {
  // State management - moved from AttorneyMattersPage
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [sections, setSections] = useState<Section[]>(initialSections);
  const sectionsRef = useRef<Section[]>(sections);

  // Keep sectionsRef in sync
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  // Generate a localStorage key specific to this link
  const getStorageKey = useCallback(() => {
    return `attorney-matters-${attorneyLinkId}`;
  }, [attorneyLinkId]);

  // Load form data from localStorage on initial render
  useEffect(() => {
    if (attorneyLinkId) {
      try {
        const savedData = localStorage.getItem(getStorageKey());
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          setFormData(parsedData);
        } else {
          // If no saved data, initialize with default matters
          setFormData({
            PM1: createEmptyMatter(),
            CM1: createEmptyMatter(),
          });
        }
      } catch (error) {
        console.error("Error loading form data from localStorage:", error);
        // Initialize with default matters on error
        setFormData({
          PM1: createEmptyMatter(),
          CM1: createEmptyMatter(),
        });
      }
      setIsLoaded(true);
    }
  }, [attorneyLinkId, getStorageKey]);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (attorneyLinkId && isLoaded) {
      try {
        localStorage.setItem(getStorageKey(), JSON.stringify(formData));
      } catch (error) {
        console.error("Error saving form data to localStorage:", error);
      }
    }
  }, [formData, attorneyLinkId, getStorageKey, isLoaded]);

  // Form handlers - moved from AttorneyMattersPage
  const handleInputChange = useCallback((id: string, value: any) => {
    setFormData((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleInputBlur = useCallback((id: string, value?: any) => {
    // Optionally add field-level saving logic here if needed
  }, []);

  const handleAddMatter = useCallback(
    (type: "publishable" | "confidential") => {
      const prefix = type === "publishable" ? "PM" : "CM";
      const currentCount = Object.keys(formData).filter((key) =>
        key.startsWith(prefix),
      ).length;
      const newIndex = currentCount + 1;
      const newId = `${prefix}${newIndex}`;
      setFormData((prev) => ({
        ...prev,
        [newId]: createEmptyMatter(),
      }));
    },
    [formData],
  );

  const handleDeleteMatter = useCallback((matterId: string) => {
    setFormData((prev) => {
      const newData = { ...prev };
      delete newData[matterId];
      return newData;
    });
  }, []);

  const clearAllData = useCallback(() => {
    if (attorneyLinkId) {
      try {
        localStorage.removeItem(getStorageKey());
        setFormData({
          PM1: createEmptyMatter(),
          CM1: createEmptyMatter(),
        });
      } catch (error) {
        console.error("Error clearing localStorage:", error);
      }
    }
  }, [attorneyLinkId, getStorageKey]);

  const updateSections = useCallback((newSections: Section[]) => {
    setSections(newSections);
  }, []);

  // Attorney-specific context value
  const attorneyContextValue = useMemo(
    () => ({
      formData,
      isLoaded,
      handleInputChange,
      handleInputBlur,
      handleAddMatter,
      handleDeleteMatter,
      clearAllData,
      updateSections,
    }),
    [
      formData,
      isLoaded,
      handleInputChange,
      handleInputBlur,
      handleAddMatter,
      handleDeleteMatter,
      clearAllData,
      updateSections,
    ],
  );

  // Submissions context for compatibility with existing components
  const dummySubmissionsContext = useMemo(
    () => ({
      isSaving: false,
      setIsSaving: (value: boolean) => {},
      submissionData: null,
      formData,
      setFormData,
      initialFormData: null,
      error: false,
      showLockWarning: () => {},
      updateLockState: (isLocked: boolean) => {},
      sidePanelOpen: null as SIDE_PANELS_AVAILABLE | null,
      openSidePanel: (
        panelName: SIDE_PANELS_AVAILABLE,
        questionId: string,
      ) => {},
      closeSidePanel: () => {},
      submissionComments: {},
      submissionRevisions: {},
      activeCommentQuestion: null,
      setActiveCommentQuestion: (sectionId: string | null) => {},
      saveField: (fieldId: string, value: any) => Promise.resolve(),
      pendingSaves: new Set<string>(),
      approvedQuestions: new Set<string>(),
      isQuestionApproved: (questionId: string) => false,
      pendingReviewQuestions: new Set<string>(),
      isQuestionPendingReview: (questionId: string) => false,
      updateField: handleInputChange,
      updateTableCell: (
        questionId: string,
        rowIndex: number,
        colIndex: number,
        value: any,
      ) => {},
      hasUnsavedChanges: false,
      updateApprovalStateAfterReorder: () => {},
      registerEditorRef: () => {},
      isFormLocked: false,
      lockField: () => {},
      unlockField: () => {},
      lockedFields: {},
      lockedQuestions: {},
      userProfiles: {},
      requestFieldUnlock: () => {},
      sections: [],
      currentlyEditingField: null,
    }),
    [formData, setFormData, handleInputChange],
  );

  return (
    <AttorneySubmissionsContext.Provider value={attorneyContextValue}>
      <SubmissionsContext.Provider value={dummySubmissionsContext}>
        {children}
      </SubmissionsContext.Provider>
    </AttorneySubmissionsContext.Provider>
  );
};

export const useAttorneySubmissions = () => {
  const context = useContext(AttorneySubmissionsContext);
  if (!context) {
    throw new Error(
      "useAttorneySubmissions must be used within an AttorneySubmissionsProvider",
    );
  }
  return context;
};

export default AttorneySubmissionsProvider;
